import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useDeleteMatter } from '@/api/mutations/useDeleteMatter';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';

interface DeleteMatterDialogProps {
  matterId: string;
  matterName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirectOnSuccess?: boolean;
}

export function DeleteMatterDialog({
  matterId,
  matterName,
  open,
  onOpenChange,
  redirectOnSuccess = false,
}: DeleteMatterDialogProps) {
  const navigate = useNavigate();
  const [confirmationText, setConfirmationText] = useState('');
  const deleteMatter = useDeleteMatter();

  const displayName = matterName || 'Untitled Matter';
  const isConfirmationValid = confirmationText === displayName;

  const handleDelete = () => {
    deleteMatter.mutate(matterId, {
      onSuccess: () => {
        onOpenChange(false);
        setConfirmationText('');
        if (redirectOnSuccess) {
          navigate('/matters');
        }
      },
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmationText('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--alert-triangle text-red-500" />
            Delete Matter
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p>
            This action cannot be undone. This will permanently delete the Matter and all its Subsets, documents, and
            data.
          </p>
          <p className="mb-6">
            Once you delete the Matter, it may take up to 30 minutes for the data to be completely removed.
          </p>

          <div>
            <p className="mb-2 text-sm text-gray-600">
              To confirm, type <span className="font-semibold text-gray-900">{displayName}</span> below:
            </p>
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type the matter name to confirm"
              className="w-full"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="default" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmationValid || deleteMatter.isLoading}
          >
            Delete Matter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
